angular.module('ui.customer.overview', ['kendo.directives', 'nvd3'])

    .config(function ($stateProvider) {
        $stateProvider.state('client.customer', {
            url: '/customers/:customerId/overview',
            templateUrl: 'app/ui/customer/overview/overview.html',
            controller: 'CustomerOverviewController',
            controllerAs: 'CustomerOverviewController',
            data: { pageTitle: 'OrderManager: Customer Overview' },
            resolve: {
                currentUser: function (usersRepository) {
                    return usersRepository.getCurrentUser();
                }
            }
        });
    })

    .controller('CustomerOverviewController', function ($window, $compile, $timeout, $rootScope, $scope, $state, $log, $q, gettext, uiLocalization, toastr, usersRepository,
        appLocale, rest, authService, currentUser, serverLocalization, customersRepository, ordersRepository, customerOverviewUpdatesService, geocodingService, workflowRepository, 
        gridDataProvider, templateResolver, orderItemsGridSharedSettings, customResourceResolver) {

        var textSearchTrigger = false;
        var dataRequestCount = 0;
        const CalendarChartTypes = Object.makeEnum(["daily", "weekly", "monthly"]);
        
        $scope.user = currentUser;
        $scope.customerId = $state.params.customerId;
        $scope.customer = null;
        $scope.customerOrders = [];
        $scope.permissionsGranted = false;
        $scope.calendarChartType = CalendarChartTypes.monthly;

        //We want the stats to show items for the previous 6 months, excluding current month
        $scope.monthsCount = 12; //Controls dynamically the length of the period we want the stats to be gathered for
        $scope.toDate = new Date();
        $scope.fromDate = new Date($scope.toDate.getFullYear(), $scope.toDate.getMonth() - $scope.monthsCount, $scope.toDate.getDate());

        _.extend($scope, {
            setBusyState: setBusyState,
            applyTextSearch: applyTextSearch,
            viewCalendarChartDaily: viewCalendarChartDaily,
            viewCalendarChartWeekly: viewCalendarChartWeekly,
            viewCalendarChartMonthly: viewCalendarChartMonthly
        });

        initController();

        function initController() {
            $scope.isBusy = true;

            //clear any remembered search filter 
            if (!$state.params.search) {
                orderItemsGridSharedSettings.setTextSearch(null);
            }

            checkCurrentUserPermissions()
                .then(function(granted) {
                    if (granted) {
                        $q.all([initPageHeaderAndConfigs(), getCustomerStatistics()])
                            .then(function(result) {

                                getAddressGeocodingData();

                                //Init watchers to watch for order changes and reload data
                                initWatchers();

                                //Init customer's orders grid
                                initAdditionalFilter();
                                initGrid();
                            })
                            .then(function() {
                                $scope.isBusy = false;
                            });

                    } else {
                        //otherwise do nothing
                        $scope.isBusy = false;
                    }
                })
                .catch(function(error) {
                    $log.error("Can't initialize customers orders data & grid.");
                    if (error && error.message) {
                        $log.error(error.message);
                        toastr.error(uiLocalization.translate("Data request failed. Error: %s", error.message));
                    } else if (error && _.isString(error)) {
                        $log.error(error);
                        toastr.error(uiLocalization.translate("Data request failed. Error: %s", error));
                    } else if (error && error.status) {
                        toastr.error(uiLocalization.translate("Data request failed. Error %s : %s",
                            error.status,
                            error.statusText));
                    } else {
                        toastr.error(gettext("Data request failed"), gettext("Server is not responding"));
                    }
                });
        }

        function initWatchers() {

            var reloadGridBySignalREvent = _.debounce(function (e) {
                reloadGrid();
                getCustomerStatistics(); 
            }, 1000);

            customerOverviewUpdatesService.onOrderItemChanged(reloadGridBySignalREvent);
            customerOverviewUpdatesService.onOrderChanged(reloadGridBySignalREvent);
            customerOverviewUpdatesService.onNewOrderItem(reloadGridBySignalREvent);

            var calendarChartTypeWatcherDestructor = $scope.$watch('calendarChartType',
                function(newVal, oldVal) {
                    if (newVal !== oldVal) {
                        $scope.$applyAsync(function() {
                            initCalendarChartData();
                        });
                    }
                });

            //Grid's 'show completed dates' filter watchers
            $scope.$watch('datePickerFrom', completedDateChangedHandler);
            $scope.$watch('datePickerTo', completedDateChangedHandler);
            $scope.$watch('showCompletedFilter', function (newVal, oldVal) {
                if (newVal !== oldVal) {
                    if (newVal === true && !$scope.datePickerFrom && !$scope.datePickerTo) {
                        var now = new Date();
                        var oneMonthAgo = new Date();
                        oneMonthAgo.setMonth(now.getMonth() - 3); //Let's use 3 month ago
                        $scope.datePickerFrom = oneMonthAgo;
                        $scope.datePickerTo = now;
                    }
                    completedDateChanged();
                    reloadGrid();
                }
            });

            $scope.$on(
                "$destroy",
                function () {
                    destroyGrid();
                    customerOverviewUpdatesService.onOrderItemChanged(null);
                    customerOverviewUpdatesService.onNewOrderItem(null);
                    customerOverviewUpdatesService.onOrderChanged(null);

                    if (calendarChartTypeWatcherDestructor) {
                        calendarChartTypeWatcherDestructor();
                    }
                }
            );
        }

        function checkCurrentUserPermissions() {
            return $q.when($scope.user) //we have user already
                .then(function(user) {
                    //Check that current user is a centre user or a customer user who has rights to access specified customer data
                    $scope.permissionsGranted = user && !user.isCustomer();

                    if (!$scope.permissionsGranted) {
                        toastr.error('Sorry, you have no access to this data :(', 'Access denied');
                        $log.error(`User [${user.$model.name}] has no permissions to view Customer [${$scope.customerId}] data.`);
                    }

                    return $scope.permissionsGranted;
                });
        }

        function initPageHeaderAndConfigs() {
            $scope.pageDisplayName = uiLocalization.translate('Customer Overview');

            return customersRepository.getCustomer($scope.customerId)
                .then(function (customer) {
                    $scope.customer = customer;

                    $q.all(
                        [
                            gridDataProvider.getGridConfig('all-open'),
                            templateResolver.resolve('app/ui/customer/overview/gridHeader.html'),
                            customResourceResolver.resolveConfig(
                                'customer-profile-open-orders-filter',
                                {
                                    //Default config if nothing resolved from customization configuration files
                                    name: $scope.customer.$model.code,
                                    color: 'green',
                                    queryForStatInfo: `OrderItem.Deleted = false AND Step.Code <> 'Complete' AND Step.Code <> 'Cancelled' AND Customer.Id = ${$scope.customerId}`,
                                    targetUri: `#/client/customers/${$scope.customerId}/overview`,
                                    iconName: 'track',
                                    uiPriority: 1
                                },
                                {
                                    //Context data for the customized config
                                    customerId: $scope.customerId,
                                    customerCode: $scope.customer.$model.code
                                }),
                            customResourceResolver.resolveConfig(
                                'customer-profile-completed-orders-filter',
                                {
                                    //Default config if nothing resolved from customization configuration files
                                    name: $scope.customer.$model.code,
                                    color: 'red',
                                    queryForStatInfo: `OrderItem.Deleted = false AND Step.Code <> 'Cancelled' AND Customer.Id = ${$scope.customerId}`,
                                    targetUri: `#/client/customers/${$scope.customerId}/overview`,
                                    iconName: 'track',
                                    uiPriority: 1
                                },
                                {
                                    //Context data for the customized config
                                    customerId: $scope.customerId,
                                    customerCode: $scope.customer.$model.code
                                })
                        ]
                    ).then(function (data) {
                        var gridConfig = _.clone(data[0]);
                        $scope.gridHeaderTemplate = data[1];
                        $scope.gridOrdersCustomFilter = data[2];
                        $scope.gridOrdersCustomCompletedFilter = data[3];

                        //Set our custom filter config onto order items grid shared settings object,
                        //so grid data provider will get us the orders according to this custom filter OMQL query
                        //for all open orders, instead of expecting us to request orders for particular step
                        orderItemsGridSharedSettings.setCustomFilter($scope.gridOrdersCustomFilter);
                        orderItemsGridSharedSettings.setStepFilter("all-open"); //to set other grid behaviour settings equal to 'all-open' filter
                        
                        gridConfig = initGridConfig(gridConfig, $scope.gridHeaderTemplate);
                        gridConfig.dataSource = initGridDataSource(gridConfig);
                        $scope.gridConfig = gridConfig;

                        if ($scope.customer) {
                            $scope.pageDisplayName = uiLocalization.translate("Customer: %s", $scope.customer.$model.address.$model.companyName);
                            $rootScope.pageTitle = uiLocalization.translate("OrderManager: Customer: %s", $scope.customer.$model.address.$model.companyName);
                        }
                    });
                });
        }

        function getCustomerStatistics() {
            var filterQuery = null;

            //TODO: Think on applying more filters
            return $q.all([workflowRepository.getSteps(), customResourceResolver.resolveWorkflow()])
                .then(function (data) {
                    var steps = data[0];
                    var workflowConfig = data[1];

                    var getOpenOrdersStats = customersRepository
                        .getCustomerOrdersStepsStats($scope.customerId, filterQuery)
                        .then(function (stats) {
                            //A bit of mapping/pre-processing before we return all orders counts per workflow stage
                            //so we can render later all values (if necessary) even for the workflow stages with 0 items
                            var stepsWithStats = _.map(steps,
                                function (step) {
                                    var statValue = stats.find(function (stat) { return stat.step === step.$model.code });
                                    return { step: step, stat: statValue ? statValue : { numOrderItems: 0, numOrders: 0} }
                                });

                            return stepsWithStats;
                        });

                    var getCalendarStats = customersRepository
                        .getCustomerOrdersHistoryStats($scope.customerId, $scope.fromDate, $scope.toDate);

                    return $q.all([getOpenOrdersStats, getCalendarStats]);
                })
                .then(function (data) {
                    processCustomerStatistics(data[0], data[1]);
                });
        }

        function processCustomerStatistics(stepsWithStats, calendarStats) {
            if (stepsWithStats) {
                //Calculate totals
                var ordersTotals = {
                    //Total order items count at all steps/stages
                    total: _.reduce(stepsWithStats, function(memo, sws) {
                         return memo + sws.stat.numOrderItems;
                    }, 0),
                    //Non completed orders/items
                    open: _.reduce(stepsWithStats, function(memo, sws) {
                         return memo + (sws.step.$model.type !== 2 ? sws.stat.numOrderItems : 0);
                    }, 0),
                    //Completed orders items
                    completed: _.reduce(stepsWithStats, function(memo, sws) {
                        return memo +
                            (sws.step.$model.type === 2 && sws.step.$model.code.toLowerCase().indexOf('cancel') === -1 ? sws.stat.numOrderItems : 0);
                    }, 0),
                    //Completed orders items
                    cancelled: _.reduce(stepsWithStats, function (memo, sws) {
                        return memo +
                            (sws.step.$model.type === 2 && sws.step.$model.code.toLowerCase().indexOf('cancel') !== -1 ? sws.stat.numOrderItems : 0);
                    }, 0)
                };

                //And assign to variables bound to UI
                $scope.$applyAsync(function () {
                    $scope.customerStepsStats = stepsWithStats;
                    $scope.ordersTotals = ordersTotals;

                    initStepsChartData();
                });
            }

            if (calendarStats) {

                $scope.$applyAsync(function () {
                    $scope.customerCalendarStats = calendarStats;

                    initCalendarChartData();

                    checkIfActiveCustomer();
                });
            }
        }

        function checkIfActiveCustomer() {
            if (!$scope.customerCalendarStats || !$scope.customerCalendarStats.stats || $scope.customerCalendarStats.stats.length === 0) return;

            const stats = $scope.customerCalendarStats.stats;
            const mostRecentOrderDate = stats[stats.length - 1];
            if (mostRecentOrderDate && mostRecentOrderDate.date) {
                $scope.isActiveCustomer = Date.diffDays(new Date(mostRecentOrderDate.date), new Date()) < 30;
            }

        }

        function initStepsChartData() {
            if (!$scope.customerStepsStats) {
                return;
            }

            $scope.chartData = [
                {
                    key: "Orders per workflow step",
                    values: _.map(
                        $scope.customerStepsStats.filter(function (ss) { return ss.stat.numOrderItems > 0 && ss.step.$model.type !== 2; }), //ignore 0 items steps and 'end' steps
                        function (sws) {
                            return {
                                label: serverLocalization.translate(sws.step.$model.nameResource),
                                value: sws.stat.numOrderItems
                            };
                        })
                }
            ];

            $scope.chartOptions = {
                chart: {
                    type: 'discreteBarChart',
                    height: 250,
                    margin: { top: 10, right: 0, bottom: 65, left: 0 },
                    x: function(d) { return d.label; },
                    y: function(d) { return d.value; },
                    forceY: [0, 10],
                    //useInteractiveGuideline: true,
                    showValues: true,
                    staggerLabels: $scope.chartData[0].values.length > 6,
                    wrapLabels: true,
                    noData: gettext('No orders data available'),
                    valueFormat: function(d) { return d3.format('~g')(d); },
                    duration: 500,
                    xAxis: {
                        //axisLabel: gettext('Open Orders')
                        //axisLabelDistance: 10,
                        //rotateLabels: 40
                    
                    },
                    showYAxis: true,
                    //yAxis: {
                    //},
                    tooltip: {
                        valueFormatter: function(d) {
                            return d > 1
                                ? uiLocalization.translate("%s Items", d3.format('~g')(d))
                                : uiLocalization.translate("%s Item", d3.format('~g')(d));
                        }
                    }
                },
                //title: {
                //    enable: true,
                //    text: gettext('Current Orders')
                //},

                //subtitle: {
                //    enable: true,
                //    text: 'Subtitle for simple line chart. Lorem ipsum dolor sit amet...',
                //    css: {
                //        'text-align': 'center',
                //        'margin': '10px 13px 0px 7px'
                //    }
                //},

                //caption: {
                //    enable: true,
                //    html: 'Figure 1. Lorem ipsum dolor sit amet...',
                //    css: {
                //        'text-align': 'justify',
                //        'margin': '10px 13px 0px 7px'
                //    }
                //}
            };
        }

        function viewCalendarChartDaily() {
            $scope.calendarChartType = CalendarChartTypes.daily;
        }
        function viewCalendarChartWeekly() {
            $scope.calendarChartType = CalendarChartTypes.weekly;
        }
        function viewCalendarChartMonthly() {
            $scope.calendarChartType = CalendarChartTypes.monthly;
        }

        function initCalendarChartData() {
            if (!$scope.customerCalendarStats) {
                return;
            }

            let values = [];
            let chartTypeName = $scope.calendarChartType.description[0].toUpperCase() + $scope.calendarChartType.description.slice(1);
            let xAxisTickFormat, tooltipKeyFormat, xLabelRotation;
            let tickValuesGetter = null;
            let tickFormatter;
            let tooltipKeyFormatter;
            let barWidthCoeff;
            let groupedValues;
            const currentLocale = currentUser.$model.culture;
            const formatter = new Intl.DateTimeFormat(currentLocale);

            switch ($scope.calendarChartType) {
                case CalendarChartTypes.weekly:
                    tooltipKeyFormat = 'Week %W, %Y'; //For weeks - used for parsing only, as tick formatter will render weeks numbers not 00-51, but 01-52.
                    xLabelRotation = -45;
                    barWidthCoeff = 0.5;
                    tickValuesGetter = function (values) {
                        return _.map(values[0].values,
                            function (v, i) {
                                return values[0].values.length > 10 ? (i % 2 === 0 ? null : v.x) : v.x; //we return empty string for each even tick, so we don't have a mess of week labels on x axis
                            });
                    };
                    tickFormatter = function (dateTicks) {
                        if (!dateTicks) return '';
                        var date = new Date(dateTicks);
                         return `W${date.getYearWeekNumber()}, ${date.getFullYear()}`;
                    };
                    tooltipKeyFormatter = function (dateTicks) {
                        var date = new Date(dateTicks);
                        return `W${date.getYearWeekNumber()}, ${date.getFullYear()}`;
                    }

                    groupedValues = _.groupBy($scope.customerCalendarStats.stats, function (s) { return new Date(s.date).setToMonday(); });

                    values = _.map(groupedValues,
                        function (groupValues, key, list) {
                            return {
                                //label for each Week as parsed from the group key
                                x: new Date(key).getTime(),
                                //value
                                y: _.reduce(groupValues,
                                    function (memo, s) {
                                        return memo + s.numOrderItems; //summarize order items count from each day of the Month group
                                    }, 0)
                            };
                        });
                    break;
                case CalendarChartTypes.monthly:
                    xAxisTickFormat = '%b %y';
                    tooltipKeyFormat = '%B %Y';
                    xLabelRotation = -45;
                    barWidthCoeff = 0.7;
                    tickValuesGetter = function (values) { return _.map(values[0].values, function (v) { return new Date(v.x); }); };
                    tickFormatter = function (d) { return d3.time.format(xAxisTickFormat)(d); };
                    tooltipKeyFormatter = function (d) { return d3.time.format(tooltipKeyFormat)(new Date(d)); }
                    
                    groupedValues = _.groupBy($scope.customerCalendarStats.stats,
                        function(s) {
                            return d3.time.format(tooltipKeyFormat)(new Date(s.date));
                        });

                    values = _.map(groupedValues,
                        function (groupValues, key, list) {
                            return { 
                                //label for Month, in ticks
                                x: new Date(key).getTime(),
                                //value
                                y: _.reduce(groupValues,
                                    function (memo, s) {
                                        return memo + s.numOrderItems; //summarize order items count from each day of the Month group
                                    }, 0)
                                };
                        });
                    
                    break;
                case CalendarChartTypes.daily:
                default:
                    tooltipKeyFormat = '%b %d, %Y';
                    xLabelRotation = -45;
                    barWidthCoeff = 0.6;
                    tickValuesGetter = null;
                    tickFormatter = function (d) { return formatter.format(d); };
                    tooltipKeyFormatter = function (d) { return d3.time.format(tooltipKeyFormat)(new Date(d)); }

                    values = _.map($scope.customerCalendarStats.stats,
                        function(s) {
                            return {x: new Date(s.date).getTime(), y: s.numOrderItems};
                        });
                    break;
            }

            $scope.chartCalendarData = [
                {
                    key: uiLocalization.translate('%s Orders History', chartTypeName),
                    bar: true,
                    values: values
                }
            ];

            $scope.chartCalendarOptions = {
                chart: {
                    type: 'historicalBarChart',
                    height: 250,
                    margin: { top: 10, right: 10, bottom: 75, left: 55 },
                    x: function (d) { return d.x; },
                    y: function (d) { return d.y; },
                    showValues: true,
                    noData: gettext('No history data available'),
                    valueFormat: function (d) { return d3.format('~g')(d); },
                    duration: 500,
                    xAxis: {
                        scale: d3.time.scale(),
                        axisLabelDistance: 5,
                        tickFormat: tickFormatter,
                        rotateLabels: xLabelRotation,
                        tickValues: tickValuesGetter,
                        showMaxMin: false
                    },
                    yAxis: {
                        axisLabelDistance: 10,
                        tickFormat: function (d) { return d3.format('~g')(d); }
                    },
                    tooltip: {
                        keyFormatter: tooltipKeyFormatter,
                        valueFormatter: function (d) {
                            return d > 1
                                ? uiLocalization.translate("%s Items", d3.format('~g')(d))
                                : uiLocalization.translate("%s Item", d3.format('~g')(d));
                        }
                    },
                    zoom: {
                        enabled: true,
                        scaleExtent: [1, 50],
                        useFixedDomain: false,
                        useNiceScale: true,
                        horizontalOff: false,
                        verticalOff: true,
                        unzoomEventType: 'dblclick.zoom'
                    },
                    bars: {
                        dispatch: {
                            renderEnd: function (t, u) {
                                var chartBars = d3.selectAll("rect.nv-bar");
                                if (chartBars.length > 0) {
                                    chartBars.style("width",
                                        function(d, i) {
                                            return (100 / (chartBars[0].length)) * barWidthCoeff + '%'; //We want to make bars a bit more narrow
                                        });
                                }
                            }
                        }
                    }
                }
            };

            $scope.onChartCalendarReady = function(scope, element) {
                
            };
        }

        function getAddressGeocodingData() {
            if ($scope.customer) {
                geocodingService.getGeocodingForAddress($scope.customer.$model.address.toFormattedString())
                    .then(function (result) {
                        $scope.customerAddressPlace = result;
                    });
            }
        }

        function initAdditionalFilter() {
            $scope.searchDropdownOptions = {
                dataSource: [
                    { text: gettext("Regular Search"), value: "reg-search", icon: "search-dropdown-icon icon-magnifier-add" },
                    { text: gettext("Exact Match Search"), value: "exact-search", icon: "search-dropdown-icon icon-magnifier" }
                ],
                valuePrimitive: false,
                dataTextField: "text",
                dataValueField: "value",
                valueTemplate: ' ',
                template: '<span class="k-state-default"><i class="{{dataItem.icon}} pull-left"></i>' +
                    '<span>{{dataItem.text}}</span></span>',
                dataBound: function () {
                    this.list.width("auto");
                }
            };

            $scope.clearSearchTerm = function () {
                orderItemsGridSharedSettings.setTextSearch(null);
                $scope.textSearch = null;
                applyTextSearch();
            };

            var textSearchConfig = orderItemsGridSharedSettings.getTextSearch();
            if (textSearchConfig) {
                $scope.textSearch = textSearchConfig.text;
                $scope.searchDropdown = textSearchConfig.exact ? "exact-search" : "reg-search";
            } else {
                $scope.textSearch = null;
                $scope.searchDropdown = "reg-search";
            }
        }

        function initDatesFilter() {

            var completedFilterConfig = orderItemsGridSharedSettings.getCompletedFilter();
            
            if (completedFilterConfig) {
                //showCompletedFilter flag value controlled by kendo-switch when turned ON by user
                //This kendo-switch will be shown in the UI if gridConfig.hideShowCompletedSwitch == false
                $scope.showCompletedFilter = !!completedFilterConfig.enabled;
                var now = new Date();
                var dateFrom = new Date();
                dateFrom.setMonth(now.getMonth() - 3); //Let's use 3 month ago
                $scope.datePickerFrom = completedFilterConfig.from || dateFrom;
                $scope.datePickerTo = completedFilterConfig.to || now;
                //Now allow the filter to be shown only for 'all-open' stage
                $scope.showCompletedFilter = !!completedFilterConfig.enabled;
            }

            if (!completedFilterConfig) {
                $scope.showCompletedFilter = false;
                $scope.datePickerFrom = null;
                $scope.datePickerTo = null;
            }
            completedDateChanged();
        }

        function initGrid() {
            angular.element($window).bind('resize', function () {
                resizeGrid();

                // manual $digest required as resize event
                // is outside of angular
                $scope.$digest();
            });

            //Init dates filtering controls in the grid header (we need to do it from here to make sure we have grid config available )
            //Before we compile grid into the view
            initDatesFilter();

            var gridTemplate = '<div id="orderItemsGrid" kendo-grid="orderItemsGrid" k-options="gridConfig" class="all-open-grid k-grid-content"></div>';
            var jqTemplate = $('#orderItemsKendoGrid').append(gridTemplate);
            $compile(jqTemplate)($scope);

            //Init all the handlers for the events of the grid (like row click, checkbox-selector click, ,double-click, etc.
            initGridEventsHandlers($scope.gridConfig);

            //Trigger window resize event to properly resize grid
            $timeout(function () { $(window).trigger('resize'); });

        }

        function initGridConfig(gConfig, gridHeaderTemplate) {

            gConfig.hideShowCompletedSwitch = true;

            if (gConfig.columns) {
                var firstVisibleColumn = _.find(gConfig.columns, function (c) {
                    return !c.hidden && c.field;
                });
                if (firstVisibleColumn) {
                    firstVisibleColumn.template = _.string.sprintf(
                        "<div style='white-space:nowrap;'><a title='%s' class='fa fa-lg fa-ellipsis-v a-fa-btn details-btn pull-left' ng-click='showStepDetails()'></a><span>#:%s#</span></div>",
                        gettext("View Details"),
                        firstVisibleColumn.field);
                }

                //Check if we have columns without percentage specified. Assign width if so.
                for (var i = 0; i < gConfig.columns.length; i++) {
                    var column = gConfig.columns[i];
                    if (!column.width) {
                        column.width = 2 * (100 / gConfig.columns.length) + "%";
                    }
                }
            }

            gConfig.toolbar = [{ template: gridHeaderTemplate }];

            gConfig.noRecords = {
                template: gettext("No items to display")
            };

            gConfig.dataBound = function (e) {
                //Execute the dataBound defined on the customised config first 
                if (angular.isDefined($scope.gridConfig.dataBoundEventHandler) && angular.isFunction($scope.gridConfig.dataBoundEventHandler)) {
                    try {
                        $scope.gridConfig.dataBoundEventHandler.call($scope.orderItemsGrid, e);
                    } catch (error) {
                        $log.error("Error during executing customised Grid's config's DataBound even handler.", error);
                    }
                }

                //And highlight the searchTerm found in the text values inside rows shown
                $timeout(function () {
                    if (!String.isNullOrWhiteSpace($scope.textSearch)) {
                        $("#orderItemsKendoGrid tbody td[role='gridcell']").highlight($scope.textSearch);
                    } else {
                        $("#orderItemsKendoGrid tbody td[role='gridcell']").unhighlight();
                    }
                });
            };

            //Grid pager is 'ON' by default or if specified so
            if (!gConfig.pageable || typeof gConfig.pageable === 'boolean' || gConfig.pageable !== null) {
                //Pager info (items count) is 'ON' by defaults unless specified to be 'OFF'
                var disablePagerInfo = gConfig.disablePagerItemsInfo && gConfig.disablePagerItemsInfo === true;
                if (disablePagerInfo) {
                    //Reset pageable object. Otherwise just not touch it as it contains other valuable settings
                    gConfig.pageable = {
                        refresh: true,
                        pageSizes: true,
                        info: !disablePagerInfo
                    };
                }
            }

            //See if we have default sorting specified, then set it onto grid shared settings
            if (angular.isDefined(gConfig.initialSort)) {
                if (angular.isArray(gConfig.initialSort)) {
                    orderItemsGridSharedSettings.setGridSort(gConfig.initialSort);
                } else if (angular.isDefined(gConfig.initialSort.field) && angular.isDefined(gConfig.initialSort.dir)) {
                    orderItemsGridSharedSettings.setGridSort([gConfig.initialSort]);
                }
            }

            return gConfig;
        }

        function completedDateChangedHandler(newVal, oldVal) {
            if (newVal !== null && !_.isEqual(newVal, oldVal)) {
                completedDateChanged();
                reloadGrid();
            }
        }

        function completedDateChanged() {
            //re-set our custom filter with added dates filtering controls (or set back to default one) if user entered completion dates filter
            if ($scope.showCompletedFilter && $scope.datePickerFrom && $scope.datePickerTo) {
                $scope.datePickerFrom.setHours(0, 0, 0, 0);
                $scope.datePickerTo.setHours(23, 59, 59, 999);

                //we need timestamp without the milliseconds part, so omql api will understand it
                //OMQL DateTimeFormat = "yyyy-MM-ddTHH:mm:ss". i.e.: AND OrderItem.Completed = #0001-01-01T00:00:00#
                var datesFilterString = ` AND OrderItem.Completed >= #${$scope.datePickerFrom.toISOString()}# AND OrderItem.Completed <= #${$scope.datePickerTo.toISOString()}#`;
                var customCompletedFilter = _.clone($scope.gridOrdersCustomCompletedFilter);
                customCompletedFilter.queryForStatInfo += datesFilterString;

                orderItemsGridSharedSettings.setCustomFilter(customCompletedFilter);
                orderItemsGridSharedSettings.setStepFilter("all-open"); //to set other grid behaviour settings equal to 'all-open' filter
            } else {
                orderItemsGridSharedSettings.setCustomFilter($scope.gridOrdersCustomFilter);
                orderItemsGridSharedSettings.setStepFilter("all-open"); //to set other grid behaviour settings equal to 'all-open' filter
            }
        }

        var forcedPageChange = false;
        var cachedResult;

        function initGridDataSource(gridConfig) {
            var dataSourceObj = {
                transport: {
                    read: function (options) {
                        if (forcedPageChange) { // prevent data request after manual page change
                            forcedPageChange = false;
                            if (cachedResult) {
                                options.success(cachedResult);
                            } else {
                                toastr.error(gettext("Data request cache error"));
                                options.error();
                            }
                            return;
                        }
                        $scope.isBusy = true;
                        $scope.isGridReload = true;

                        //Reset page size ignorance flag as it shouldn't alter grid behavior.
                        //It is applied only on the item details card if order item id specified in the url as a parameter
                        orderItemsGridSharedSettings.setForceIgnorePageSize(false);

                        orderItemsGridSharedSettings.setPageNumber(options.data.page || 1);
                        orderItemsGridSharedSettings.setPageSize(angular.isDefined(options.data.pageSize) ? options.data.pageSize : 20);
                        orderItemsGridSharedSettings.setGridFilter(options.data.filter);
                        orderItemsGridSharedSettings.setGridSort(options.data.sort);

                        renderSkeletonPlaceholdersLoadingAnimation();

                        gridDataProvider.getData()
                            .then(function (result) {
                                orderItemsGridSharedSettings.setTotalCount(result.totalCount);
                                orderItemsGridSharedSettings.setTotalPageCount(result.totalPageCount);
                                cachedResult = result;
                                dataRequestCount++;

                                // check if current page number was changed
                                if ($scope.gridConfig.dataSource.page() !== result.pageNumber ||
                                    $scope.gridConfig.dataSource.pageSize() !== result.rowsPerPage) {
                                    forcedPageChange = true;
                                    options.page = result.pageNumber;
                                    options.pageSize = result.rowsPerPage;
                                    $scope.gridConfig.dataSource.query(options);
                                }

                                if (!$scope.selectedOrderItemId) {
                                    $scope.selectedOrderItemId = orderItemsGridSharedSettings.getSelectedOrderItemId();
                                }

                                clearSkeletonPlaceholdersLoadingAnimation();

                                options.success(result);
                                $scope.isBusy = false;
                                $scope.isGridReload = false;
                                textSearchTrigger = false;

                                resizeGrid();
                            },
                                function (resp) {
                                    if (resp && resp.status) {
                                        toastr.error(resp.message, gettext("Reading orders data failed"));
                                    } else {
                                        toastr.error(gettext("Reading orders data failed"), gettext("Server is not responding"));
                                    }
                                    options.error();
                                    $scope.isBusy = false;
                                    $scope.isGridReload = false;
                                    textSearchTrigger = false;
                                });
                    }
                },
                schema: {
                    data: function (response) {
                        return response ? response.items : null;
                    },
                    total: function (response) {
                        return response ? response.totalCount : 0;
                    },
                    model: {
                        id: "$model.id",
                        fields: gridConfig.fields || {}
                    }
                },
                page: orderItemsGridSharedSettings.getPageNumber() || 1,
                pageSize: orderItemsGridSharedSettings.getPageSize() || gridConfig.pageSize || 20,
                filter: orderItemsGridSharedSettings.getGridFilter(),
                sort: orderItemsGridSharedSettings.getGridSort(),
                serverPaging: true,
                serverSorting: true,
                serverFiltering: true
            };

            if (gridConfig.groupable === true && angular.isDefined(gridConfig.initialGroup)) {
                dataSourceObj.group = gridConfig.initialGroup;
                dataSourceObj.pageSize = gridConfig.pageSize;
            }

            return new kendo.data.DataSource(dataSourceObj);
        }

        function initGridEventsHandlers(gConfig) {
            //double click event to allow user to open order details
            $("#orderItemsKendoGrid tbody[role='rowgroup']").on("dblclick", "tr", function () {
                showStepDetails();
            });
        }

        /**
         * Updates busy state
         */
        function setBusyState(newBusyState) {
            $scope.isBusy = newBusyState;
        }

        function applyTextSearch() {
            orderItemsGridSharedSettings.setTextSearch($scope.textSearch, $scope.searchDropdown === "exact-search");
            reloadGrid();
            textSearchTrigger = true;
        }

        /**
         * Get selected items from grid
         * @returns {!RepresentationModel[]}
         */
        function getSelectedEntities() {
            var selectedEntities = [];
            var selectedDOM = $scope.orderItemsGrid.select();
            _.forEach(selectedDOM, function (value) {
                selectedEntities.push($scope.orderItemsGrid.dataItem(value));
            });
            return selectedEntities;
        }

        function showStepDetails() {
            var selected = getSelectedEntities();
            if (!selected || $scope.forbidOrderDetailsView) {
                //Do not allow to go into step details if nothing selected
                //OR if we are requested to do so according to the customisation configs
                return;
            }
            var firstSelected = selected[0];
            if (firstSelected && firstSelected.isRepresentationModel) {
                orderItemsGridSharedSettings.setSelectedOrderItemId(firstSelected.getId());
                // go to the Step Details View
                $state.go('client.step', {
                    step: $scope.step
                });
            }
        }

        function renderSkeletonPlaceholdersLoadingAnimation() {
            if ($('#orderItemsKendoGrid table tbody').children().length > 0 || $('#orderItemsKendoGrid div.k-grid-norecords').length > 0) {
                //Do not render skeleton loading placeholders if we do have records to show OR if instead there are no records to show and we alreayd know it
                return;
            }

            var pageSize = orderItemsGridSharedSettings.getPageSize();
            if (!angular.isDefined(pageSize)) {
                pageSize = $scope.gridConfig.pageSize;
            }

            if (pageSize === 0 || pageSize > 15) {
                pageSize = 15; //Set page size for the loading-placeholders content to 15 if it's configured for bigger value or 0 which is 'show all'
            }

            var columnsCount = 10; //10 by default but it's better to try and find the columns count from grid template
            //which is initialised based on the grid branded config
            if ($('#orderItemsKendoGrid table colgroup').length > 0) {
                columnsCount = $('#orderItemsKendoGrid table colgroup').children().length;
            }
            for (var i = 0; i < pageSize; i++) {

                //Render visual templates to indicate loading rows/cells
                var row = $('<tr></tr>');
                for (var c = 0; c < columnsCount; c++) {
                    var divWidth = c > 0 ? (40 + Math.floor(Math.random() * 60)) : 100;
                    row.append($('<td class="loading-td"><div style="width:' + divWidth + '%;"></div></td>'));
                }
                $('#orderItemsKendoGrid table tbody').append(row);

            }
        }

        function clearSkeletonPlaceholdersLoadingAnimation() {
            $('#orderItemsKendoGrid table tbody').empty();
        }

        function resizeGrid() {
            var availableHeight = $(window).height() - $("div.grid-view-title").outerHeight(true) - $("div.customer-info").outerHeight(true) 
                - $("div.k-grid-toolbar").outerHeight(true) - $("thead.k-grid-header").outerHeight(true) - $("div.k-grouping-header").outerHeight(true)
                - $("div.k-grid-pager").outerHeight(true) - $("#header").outerHeight(true)
                - parseFloat($(".orders-list").css('margin-top'));

            $('#orderItemsKendoGrid table').css("height", availableHeight);
        }

        /**
         * Force grid to refresh (if loaded)
         */
        function reloadGrid() {

            if ($scope.orderItemsGrid && $scope.orderItemsGrid.dataSource && $scope.orderItemsGrid.dataSource.read) {
                $('thead tr').find('input[type=checkbox]').prop('checked', false);
                $('thead tr').find('input[type=checkbox]').prop('indeterminate', false);

                return $scope.orderItemsGrid.dataSource.read();
            }

            return $q.when(null);
        }

        function destroyGrid() {
            if (!$scope.orderItemsGrid || !$scope.orderItemsGrid.destroy) {
                return;
            }
            $scope.orderItemsGrid.destroy();
        }

    })
    // Separate service for update events (to prevent multiple event subscription)
    .service('customerOverviewUpdatesService', function (rest) {
        var _this = this;
        var newOrderItemCb;
        var orderItemChangedCb;
        var orderChangedCb;

        rest.events.on("modelCreatedOnServer", function (modelInfo) {
            if (!newOrderItemCb) {
                return;
            }
            if (modelInfo && modelInfo.modelType === "OrderItemModel") {
                newOrderItemCb(modelInfo);
            }
        });

        rest.events.on("modelUpdatedOnServer", function (modelInfo) {
            if (modelInfo.modelType === "OrderItemModel" && orderItemChangedCb) {
                orderItemChangedCb(modelInfo);
            } else if (modelInfo.modelType === "OrderModel" && orderChangedCb) {
                orderChangedCb(modelInfo);
            }
        });

        _this.onNewOrderItem = function (cb) {
            newOrderItemCb = cb;
        };

        _this.onOrderItemChanged = function (cb) {
            orderItemChangedCb = cb;
        };

        _this.onOrderChanged = function (cb) {
            orderChangedCb = cb;
        };

        return _this;
    })
 ;
